var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { serverConnection } from "../server/ServerConnectionService";
import { usersService } from "../UsersService";
import { WebRTC } from "../mediaStream/WebRTC";
import { getOrCreateModelFromAbsolutePath } from "@modules/model";
export class PeerRTCMessageHandler {
    constructor(peerMgr) {
        this.peerMgr = peerMgr;
        this.handleServerMessage = (msg) => {
            switch (msg.channel) {
                case "rtc:offer":
                    void this.handleOffer(msg);
                    break;
                case "rtc:answer":
                    void this.handleAnswer(msg);
                    break;
                case "rtc:ice":
                    void this.handleIce(msg);
                    break;
                case "rtc:newConsumer":
                    void this.handleNewConsumer(msg);
                    break;
                case "rtc:negotiate":
                    void this.handleNegotiate(msg);
                    break;
            }
        };
        serverConnection.messageDelegate.add(this.handleServerMessage);
    }
    handleOffer(data) {
        return __awaiter(this, void 0, void 0, function* () {
            const member = data.payload.member;
            try {
                let peer = this.peerMgr.getPeer(member.clientId);
                if (peer === undefined) {
                    peer = yield this.peerMgr.createPeer(usersService.getLocalUser().clientId, member.clientId, member.clientIdx, false);
                    const room = getOrCreateModelFromAbsolutePath(member.roomModelAbsolutePath);
                    peer.onRoomChanged(room.getUID());
                }
                let transport = peer.transportMap.get(data.payload.id);
                if (transport === undefined) {
                    transport = yield peer.waitForTransport(data.payload.id);
                    if (transport === undefined) {
                        throw new Error(`Can't find transport for id ${data.payload.id}`);
                    }
                }
                if (transport instanceof WebRTC) {
                    yield transport.handleOffer(data.payload.sdp);
                }
                else {
                    const recvTransport = peer.transportMap.get("recv");
                    if (recvTransport === undefined) {
                        yield peer.waitForTransport("recv");
                    }
                    yield peer.mediaConnections.handleOffer(data.payload.sdp, data.payload.mediaId);
                }
            }
            catch (error) {
                console.error("[multiplayer] Error while handling offer from RTC server", error);
            }
        });
    }
    handleAnswer(data) {
        return __awaiter(this, void 0, void 0, function* () {
            const remoteClientId = data.payload.source;
            try {
                const peer = this.peerMgr.getPeer(remoteClientId);
                if (peer) {
                    let transport = peer.transportMap.get(data.payload.id);
                    if (!transport) {
                        transport = yield peer.waitForTransport(data.payload.id);
                        if (!transport) {
                            throw new Error(`Can't find means of transport for id ${data.payload.id}`);
                        }
                    }
                    if (transport instanceof WebRTC) {
                        yield transport.handleAnswer(data.payload.sdp);
                    }
                    else {
                        yield peer.mediaConnections.handleAnswer(data.payload.sdp);
                    }
                }
                else {
                    console.warn("[multiplayer] Got answer from the RTC server for an unknown peer", remoteClientId);
                }
            }
            catch (error) {
                console.error("[multiplayer] Error while handling answer from RTC server", error);
            }
        });
    }
    handleIce(data) {
        return __awaiter(this, void 0, void 0, function* () {
            const remoteClientId = data.payload.source;
            try {
                const peer = this.peerMgr.getPeer(remoteClientId);
                if (peer !== undefined) {
                    try {
                        let transport = peer.transportMap.get(data.payload.id);
                        if (!transport) {
                            transport = yield peer.waitForTransport(data.payload.id);
                            if (!transport) {
                                throw new Error(`Can't find transport for id ${data.payload.id}`);
                            }
                        }
                        if (transport instanceof WebRTC) {
                            yield transport.handleIce(data.payload.candidate);
                        }
                        else {
                            yield peer.mediaConnections.handleICE(transport, data.payload.candidate);
                        }
                    }
                    catch (error) {
                        console.error("[multiplayer] Error while handling ICE message", error);
                    }
                }
                else {
                    console.warn(`[multiplayer] Cannot handle ICE message since peer "${remoteClientId}" is unknown`);
                }
            }
            catch (error) {
                console.error("[multiplayer] Error while handling ICE message", error);
            }
        });
    }
    handleNewConsumer(msg) {
        return __awaiter(this, void 0, void 0, function* () {
            const member = msg.payload.member;
            try {
                let peer = this.peerMgr.getPeer(member.clientId);
                if (peer === undefined) {
                    console.warn(`[multiplayer] Can't handle new consumer since peer ${member.clientId} is undefined. Creating new peer.`);
                    peer = yield this.peerMgr.createPeer(usersService.getLocalUser().clientId, member.clientId, member.clientIdx, false);
                    const room = getOrCreateModelFromAbsolutePath(member.roomModelAbsolutePath);
                    peer.onRoomChanged(room.getUID());
                }
                let transport = peer.transportMap.get(msg.payload.id);
                if (!transport) {
                    console.warn(`[multiplayer] Can't handle new consumer since there is no available means of transport for ${msg.payload.id}, waiting for it`);
                    transport = yield peer.waitForTransport(msg.payload.id);
                    if (!transport) {
                        throw new Error(`Can't handle new consumer since there is no available means of transport for ${msg.payload.id}`);
                    }
                }
                yield peer.mediaConnections.onNewConsumer(msg.payload.producerId, msg.payload.mediaId, msg.payload.kind, msg.payload.sdp);
            }
            catch (error) {
                console.error("[multiplayer]", error);
                serverConnection.send({
                    channel: "rtc:answer",
                    payload: {
                        id: msg.payload.id,
                        sdp: "",
                        target: member.clientId
                    }
                });
            }
        });
    }
    handleNegotiate(data) {
        return __awaiter(this, void 0, void 0, function* () {
            const remoteClientId = data.payload.source;
            try {
                const peer = this.peerMgr.getPeer(remoteClientId);
                if (!peer) {
                    console.warn(`[multiplayer] Cannot handle negotiation since peer "${remoteClientId}" is undefined`);
                    return;
                }
                yield peer.mediaConnections.handleNegotiate();
            }
            catch (error) {
                console.error("[multiplayer] Error while handling negotiation", error);
            }
        });
    }
}
